<script setup lang="ts">
/**
 * UiKitRadio — радио-кнопка HeroUI UI-kit. Использовать внутри UiKitRadioGroup.
 * Figma: 483-43628. Цвета только из токенов (классы hui-*).
 */
import { BG, BORDER, RING, type HuiSize } from './palette'
import { UI_KIT_RADIO_GROUP, type RadioGroupContext } from './radioContext'

const props = withDefaults(
  defineProps<{
    value: string | number
    description?: string
    disabled?: boolean
  }>(),
  { description: '', disabled: false },
)

defineOptions({ name: 'UiKitRadio' })

const group = inject<RadioGroupContext | null>(UI_KIT_RADIO_GROUP, null)

const color = computed(() => (group?.invalid.value ? 'danger' : group?.color.value ?? 'primary'))
const size = computed<HuiSize>(() => group?.size.value ?? 'md')
const isDisabled = computed(() => props.disabled || (group?.disabled.value ?? false))
const checked = computed(() => group?.selected.value === props.value)

function select() {
  if (isDisabled.value) return
  group?.select(props.value)
}

const RING_SIZE: Record<HuiSize, string> = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-6 h-6' }
const DOT_SIZE: Record<HuiSize, string> = { sm: 'w-1.5 h-1.5', md: 'w-2 h-2', lg: 'w-2.5 h-2.5' }
const TEXT_SIZE: Record<HuiSize, string> = { sm: 'text-sm', md: 'text-base', lg: 'text-lg' }

const ringClass = computed(() => [
  'relative inline-flex items-center justify-center shrink-0 rounded-full border-2 transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-hui-background',
  RING_SIZE[size.value],
  RING[color.value],
  checked.value ? BORDER[color.value] : 'border-hui-default-300',
])
</script>

<template>
  <label
    class="relative inline-flex items-start gap-2 select-none"
    :class="isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
  >
    <input
      type="radio"
      class="sr-only"
      :name="group?.name"
      :value="value"
      :checked="checked"
      :disabled="isDisabled"
      @change="select"
    />
    <span :class="ringClass" :tabindex="isDisabled ? -1 : 0" @keydown.space.prevent="select">
      <span
        class="rounded-full transition-transform duration-150"
        :class="[DOT_SIZE[size], BG[color], checked ? 'scale-100' : 'scale-0']"
      />
    </span>
    <span v-if="$slots.default || description" class="flex flex-col leading-tight">
      <span v-if="$slots.default" :class="[TEXT_SIZE[size], 'text-hui-foreground']"><slot /></span>
      <span v-if="description" class="text-xs text-hui-foreground/60 mt-0.5">{{ description }}</span>
    </span>
  </label>
</template>
